import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Paperclip } from "lucide-react";
import { StatusBadge, brl, fdate } from "@/pages/portal/Dashboard";

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm text-slate-200">{value || "—"}</div>
    </div>
  );
}

const fdt = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [fin, setFin] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/customers/${id}`);
        setData(data);
        const finRes = await api.get(`/receivables?customer_id=${id}`);
        setFin(finRes.data);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [id]);

  if (!data) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  const { customer: c, quotes, status_history } = data;
  const files = quotes.flatMap((q) => (q.files || []).map((f) => ({ ...f, quote: q.number })));
  const stats = [
    { label: "Orçamentos", value: quotes.length, testId: "cust-stat-quotes" },
    { label: "Valor total", value: brl(quotes.reduce((s, q) => s + (q.value || 0), 0)), testId: "cust-stat-value" },
    { label: "Em aberto", value: quotes.filter((q) => ["Em análise", "Em orçamento", "Orçamento enviado", "Aguardando aprovação"].includes(q.status)).length, testId: "cust-stat-open" },
    { label: "Aprovados", value: quotes.filter((q) => q.status === "Aprovado").length, testId: "cust-stat-approved" },
    { label: "Concluídos", value: quotes.filter((q) => q.status === "Concluído").length, testId: "cust-stat-done" },
  ];

  return (
    <div data-testid="customer-detail-page">
      <Link to="/clientes-admin" data-testid="customer-back-link"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 hover:text-orange-400 transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Clientes
      </Link>

      <div className="mt-3">
        <h1 className="display text-3xl font-extrabold uppercase" data-testid="customer-name">{c.name}</h1>
        <p className="mono mt-1 text-sm text-slate-500" data-testid="customer-document">{c.document_fmt} · {c.person_type === "PF" ? "Pessoa Física" : "Pessoa Jurídica"}</p>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="card-tactical rounded-md p-4" data-testid={s.testId}>
            <div className="display text-2xl font-extrabold">{s.value}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 grid lg:grid-cols-2 gap-5">
        <section className="card-tactical rounded-md p-6" data-testid="customer-data">
          <h2 className="display text-lg font-bold uppercase tracking-wide">Dados cadastrais</h2>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <Field label="Nome / Razão Social" value={c.name} />
            <Field label="Nome Fantasia" value={c.trade_name} />
            <Field label="E-mail" value={c.email} />
            <Field label="Telefone" value={c.phone} />
            <Field label="WhatsApp" value={c.whatsapp} />
            <Field label="Endereço" value={c.address} />
            <Field label="Cidade" value={c.city} />
            <Field label="Estado" value={c.state} />
            <Field label="Cliente desde" value={fdate(c.created_at)} />
            <Field label="Última solicitação" value={fdate(c.last_request_at)} />
          </div>
        </section>

        <section className="card-tactical rounded-md p-6" data-testid="customer-status-history">
          <h2 className="display text-lg font-bold uppercase tracking-wide">Histórico de alterações de status</h2>
          {!status_history.length ? (
            <p className="mt-4 text-sm text-slate-500">Nenhuma alteração registrada.</p>
          ) : (
            <ul className="mt-4 space-y-2.5">
              {status_history.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-3 text-sm border-b border-slate-800/60 pb-2.5" data-testid={`customer-event-${e.id}`}>
                  <div>
                    <span className="mono text-slate-300">{e.quote_number}</span>
                    <span className="text-slate-500"> · {e.prev_status || "—"} → <span className="text-orange-400">{e.new_status}</span></span>
                  </div>
                  <span className="mono text-xs text-slate-600 whitespace-nowrap">{fdt(e.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="mt-5 card-tactical rounded-md overflow-hidden" data-testid="customer-quotes">
        <div className="px-6 pt-5 pb-1">
          <h2 className="display text-lg font-bold uppercase tracking-wide">Histórico de solicitações</h2>
        </div>
        {!quotes.length ? (
          <p className="px-6 pb-6 pt-2 text-sm text-slate-500">Nenhuma solicitação vinculada.</p>
        ) : (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="px-6 py-3 font-medium">Número</th>
                  <th className="px-6 py-3 font-medium">Data</th>
                  <th className="px-6 py-3 font-medium">Serviços</th>
                  <th className="px-6 py-3 font-medium">Peça</th>
                  <th className="px-6 py-3 font-medium text-right">Qtd.</th>
                  <th className="px-6 py-3 font-medium text-right">Valor</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {quotes.map((q) => (
                  <tr key={q.id} className="hover:bg-slate-900/50 transition-colors" data-testid={`customer-quote-${q.number}`}>
                    <td className="px-6 py-3 mono text-slate-200">{q.number}</td>
                    <td className="px-6 py-3 text-slate-400">{fdate(q.created_at)}</td>
                    <td className="px-6 py-3 text-slate-400 max-w-[200px] truncate">{(q.services?.length ? q.services.join(" + ") : q.service_type) || "—"}</td>
                    <td className="px-6 py-3 text-slate-400 max-w-[160px] truncate">{q.part_name}</td>
                    <td className="px-6 py-3 mono text-right text-slate-300">{q.quantity}</td>
                    <td className="px-6 py-3 mono text-right text-slate-200">{q.value != null ? brl(q.value) : "—"}</td>
                    <td className="px-6 py-3"><StatusBadge status={q.status} /></td>
                    <td className="px-6 py-3 text-right">
                      <Link to={`/solicitacoes/${q.id}`} className="text-xs font-semibold uppercase tracking-wider text-orange-500 hover:text-orange-400">
                        Abrir
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-5 card-tactical rounded-md overflow-hidden" data-testid="customer-receivables">
        <div className="p-6 pb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="display text-lg font-bold uppercase tracking-wide">Contas a Receber</h2>
          {fin && (
            <div className="mono text-[11px] text-slate-500" data-testid="customer-fin-summary">
              em aberto <span className="text-sky-400">{brl((fin.summary?.open_total || 0) + (fin.summary?.partial_total || 0))}</span>
              {" · "}vencido <span className="text-red-400">{brl(fin.summary?.overdue_total)}</span>
              {" · "}recebido <span className="text-emerald-400">{brl(fin.summary?.paid_total)}</span>
            </div>
          )}
        </div>
        {fin && (fin.items || []).length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-t border-b border-slate-800">
                <th className="px-6 py-2.5">Vencimento</th>
                <th className="px-6 py-2.5">Documentos</th>
                <th className="px-6 py-2.5">Parcela</th>
                <th className="px-6 py-2.5 text-right">Valor</th>
                <th className="px-6 py-2.5 text-right">Em aberto</th>
                <th className="px-6 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {fin.items.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/50 hover:bg-slate-900/40 transition-colors"
                  data-testid={`customer-receivable-${r.id}`}>
                  <td className="px-6 py-2.5 mono text-xs text-slate-300">
                    {fdate(r.due_date)}
                    {r.paid_at && <div className="text-[10px] text-emerald-500">pago em {fdate(r.paid_at)}</div>}
                  </td>
                  <td className="px-6 py-2.5 mono text-[11px] text-slate-500">
                    {r.quote_number} · {r.op_number} · {r.receipt_number} (R{r.receipt_seq})
                  </td>
                  <td className="px-6 py-2.5 mono text-xs text-slate-300">{r.installment}/{r.installments_total}</td>
                  <td className="px-6 py-2.5 mono text-xs text-right text-slate-200">{brl(r.amount_original)}</td>
                  <td className="px-6 py-2.5 mono text-xs text-right text-orange-400">{brl(r.amount_open)}</td>
                  <td className="px-6 py-2.5 text-xs text-slate-400">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-6 pb-6 text-xs text-slate-600">Nenhum recebível gerado para este cliente — os lançamentos nascem das retiradas de peças.</p>
        )}
      </section>

      <section className="mt-5 card-tactical rounded-md p-6" data-testid="customer-files">
        <h2 className="display text-lg font-bold uppercase tracking-wide">Arquivos enviados ({files.length})</h2>
        {!files.length ? (
          <p className="mt-3 text-sm text-slate-500">Nenhum arquivo enviado.</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {files.map((f, i) => (
              <li key={i} className="flex items-center gap-2.5 text-sm text-slate-300">
                <Paperclip className="w-4 h-4 text-orange-500 shrink-0" />
                <span className="truncate">{f.original_filename}</span>
                <span className="mono text-xs text-slate-600 shrink-0">{f.quote}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
